import React from "react";
import { useLocation } from "react-router-dom";

/**
 * Catches any render error in the tree and shows a graceful fallback
 * instead of a blank screen. Critical for a production site — without
 * this, ONE bad component crashes the entire app and visitors see
 * nothing. Logs to console + window.__lastError__ for debugging.
 *
 * Auto-resets on route change so a crash on one page doesn't poison
 * the rest of the site.
 */
class ErrorBoundaryClass extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
    if (typeof window !== "undefined") {
      window.__lastError__ = { message: String(error?.message || error), stack: error?.stack, info };
    }
  }

  componentDidUpdate(prevProps) {
    // Reset on every route change so a crash doesn't poison subsequent pages.
    if (this.state.hasError && prevProps.pathname !== this.props.pathname) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-aviana-paper px-6">
        <div className="max-w-md text-center">
          <p className="overline-plum mb-3">Something went sideways</p>
          <h1 className="font-display text-4xl text-aviana-ink mb-4">
            We're <em className="italic font-serif gold-text">on it.</em>
          </h1>
          <p className="text-aviana-muted leading-[1.7] mb-7 font-serif italic">
            A small hiccup is preventing this view from loading. Try refreshing
            the page — or visit us at the link below.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => window.location.reload()}
              className="btn-gold"
              data-testid="error-boundary-reload"
            >
              Refresh
            </button>
            <a href="/" className="btn-secondary" data-testid="error-boundary-home">
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default function ErrorBoundary({ children }) {
  // Outside Router we have no useLocation — fall back to a single window pathname
  let pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    pathname = useLocation().pathname;
  } catch (_) { /* not inside Router */ }
  return <ErrorBoundaryClass pathname={pathname}>{children}</ErrorBoundaryClass>;
}
